import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Card, Table, Modal, Descriptions, Select, Image, Spin, Tag, InputNumber, Input, Collapse } from "antd";
import { DataBadge, AppPagination } from "../../components/ui";
import { notify } from "../../components/ui/notify";
import { solicitudesCambioService } from "../../components/solicitudes-cambio/solicitudesCambioService";
import { wizardService } from "../../components/solicitudes-cambio/wizardService";
import { PATH } from "../../redux/utils";

import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";

dayjs.locale("es");

const PAGE_SIZE = 20;

const TIPO_LABEL = { error: "Error", cambio_visual: "Cambio visual", nueva_funcion: "Nueva función", otro: "Otro" };
const PRIORIDAD_LABEL = { urgente: "Urgente", normal: "Normal", cuando_se_pueda: "Cuando se pueda" };
const ESTADO_LABEL = {
  recibida: "Recibida",
  en_revision: "En revisión",
  aprobada: "Aprobada",
  fuera_de_alcance: "Fuera de alcance",
  rechazada: "Rechazada",
  en_staging: "En staging",
  lista_para_validar: "Lista para validar",
  publicada: "Publicada",
  cotizacion_enviada: "Cotización enviada",
};
const ESTADO_COLOR = {
  recibida: "#6B7280",
  en_revision: "#D97706",
  aprobada: "#0A539A",
  fuera_de_alcance: "#9333EA",
  rechazada: "#DC2626",
  en_staging: "#2563EB",
  lista_para_validar: "#16A34A",
  publicada: "#15803D",
  cotizacion_enviada: "#CA8A04",
};
const PRIORIDAD_COLOR = { urgente: "#DC2626", normal: "#0A539A", cuando_se_pueda: "#6B7280" };

const formatFecha = (d) => {
  if (!d) return "—";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD MMM YYYY, HH:mm") : String(d);
};

function CotizacionForm({ detalle, onActualizado }) {
  const [monto, setMonto] = useState(detalle.cotizacion_monto || undefined);
  const [nota, setNota] = useState(detalle.nota_proveedor || "");
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    try {
      const actualizado = await solicitudesCambioService.enviarCotizacion(detalle.id, { monto, nota });
      notify.success("Cotización enviada al cliente.");
      onActualizado(actualizado);
    } catch (err) {
      notify.error("No se pudo enviar la cotización.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mb-3 p-3" style={{ background: "#F9FAFB", borderRadius: 8 }}>
      <div className="fw-semibold mb-2">Enviar cotización</div>
      <div className="d-flex gap-2 flex-wrap mb-2">
        <InputNumber min={0} prefix="$" placeholder="Monto" value={monto} onChange={setMonto} style={{ width: 160 }} />
      </div>
      <Input.TextArea rows={2} placeholder="Nota para el cliente (opcional)" value={nota} onChange={(e) => setNota(e.target.value)} className="mb-2" />
      <Button type="primary" loading={enviando} onClick={enviar}>Enviar cotización</Button>
      {detalle.estado === "cotizacion_enviada" && (
        <div className="small text-secondary mt-2">
          Respuesta del cliente: {detalle.cotizacion_respuesta === "pendiente" ? "sin responder todavía" : detalle.cotizacion_respuesta}
        </div>
      )}
    </div>
  );
}

function FichaTecnica({ idSolicitud }) {
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    wizardService.obtenerFichaTecnicaPorSolicitud(idSolicitud)
      .then(setFicha)
      .catch(() => setFicha(null))
      .finally(() => setLoading(false));
  }, [idSolicitud]);

  if (loading) return <div className="text-center py-2"><Spin size="small" /></div>;
  if (!ficha) return null;

  return (
    <Collapse
      className="mb-3"
      items={[{
        key: "ficha",
        label: "Ficha técnica (solo HerrSoft)",
        children: (
          <div style={{ fontSize: 13 }}>
            <div className="mb-2"><strong>Propósito:</strong> {ficha.proposito || "—"}</div>
            <div className="mb-2"><strong>Vistas del molde:</strong> {ficha.vistas.join(", ")}</div>
            {ficha.datos.length > 0 && (
              <div className="mb-2">
                <strong>Datos sugeridos:</strong>
                <ul className="mb-0">
                  {ficha.datos.map((d, i) => (
                    <li key={i}>
                      {d.nombre_cliente} → <code>{d.nombre_tecnico_sugerido}</code> ({d.tipo_sugerido}, {d.nota}{d.obligatorio ? ", obligatorio" : ", opcional"})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ficha.estados_y_flujo?.estados?.length > 0 && (
              <div className="mb-2"><strong>Estados:</strong> {ficha.estados_y_flujo.estados.join(", ")}</div>
            )}
            {ficha.relaciones_con_otros_modulos?.modulos?.length > 0 && (
              <div className="mb-2"><strong>Relacionado con:</strong> {ficha.relaciones_con_otros_modulos.modulos.join(", ")}</div>
            )}
            {ficha.no_se_decidelo_tu.length > 0 && (
              <div className="mb-2"><strong>"No sé, decídelo tú" en:</strong> {ficha.no_se_decidelo_tu.join(", ")}</div>
            )}
            {ficha.urgencia && <div className="mb-2"><strong>Urgencia:</strong> {ficha.urgencia}</div>}
          </div>
        ),
      }]}
    />
  );
}

function DetalleModal({ idSolicitud, onClose }) {
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState("");

  const cargar = () => {
    if (!idSolicitud) return;
    setLoading(true);
    setError("");
    solicitudesCambioService
      .obtenerDetalle(idSolicitud)
      .then(setDetalle)
      .catch(() => setError("No se pudo cargar el detalle de la solicitud."))
      .finally(() => setLoading(false));
  };

  useEffect(cargar, [idSolicitud]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={!!idSolicitud} onCancel={onClose} footer={null} title={detalle ? `${detalle.folio} · ${detalle.titulo}` : "Solicitud"} width={720}>
      {loading && <div className="text-center py-4"><Spin /></div>}
      {!loading && error && <div className="text-danger">{error}</div>}
      {!loading && detalle && (
        <>
          <Descriptions column={2} size="small" bordered className="mb-3">
            <Descriptions.Item label="Tipo">{TIPO_LABEL[detalle.tipo] || detalle.tipo}</Descriptions.Item>
            <Descriptions.Item label="Prioridad">{PRIORIDAD_LABEL[detalle.prioridad] || detalle.prioridad}</Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Tag color={ESTADO_COLOR[detalle.estado]}>{ESTADO_LABEL[detalle.estado] || detalle.estado}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Enviada">{formatFecha(detalle.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Usuario">{detalle.usuario_nombre || `#${detalle.id_user}`}</Descriptions.Item>
            <Descriptions.Item label="Correo">{detalle.usuario_correo || "—"}</Descriptions.Item>
            <Descriptions.Item label="Vista">{detalle.vista || "—"}</Descriptions.Item>
            <Descriptions.Item label="Pestaña">{detalle.pestana || "—"}</Descriptions.Item>
            <Descriptions.Item label="Ruta" span={2}>{detalle.ruta || "—"}</Descriptions.Item>
            <Descriptions.Item label="Resolución">{detalle.resolucion || "—"}</Descriptions.Item>
            <Descriptions.Item label="Versión de la app">{detalle.version_app || "—"}</Descriptions.Item>
          </Descriptions>

          {detalle.tipo === "nueva_funcion" && <FichaTecnica idSolicitud={detalle.id} />}

          <div className="fw-semibold mb-1">{detalle.resumen_cliente ? "Resumen del cliente" : "Descripción"}</div>
          <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{detalle.resumen_cliente || detalle.descripcion}</div>

          {detalle.nota_proveedor && detalle.estado !== "cotizacion_enviada" && (
            <>
              <div className="fw-semibold mb-1">Nota del proveedor</div>
              <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{detalle.nota_proveedor}</div>
            </>
          )}

          {[...(detalle.archivos || []), ...(detalle.archivos_wizard || [])].length > 0 && (
            <>
              <div className="fw-semibold mb-2">Imágenes y archivos</div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {(detalle.archivos || []).map((a) => (
                  <ImagenProtegida key={`c-${a.id}`} url={`${PATH}${a.url}`} alt={a.tipo} />
                ))}
                {(detalle.archivos_wizard || []).map((a) => (
                  <ImagenProtegida key={`w-${a.id}`} url={`${PATH}${a.url}`} alt={a.nombre_original} />
                ))}
              </div>
            </>
          )}

          {detalle.tipo === "nueva_funcion" && <CotizacionForm detalle={detalle} onActualizado={setDetalle} />}
        </>
      )}
    </Modal>
  );
}

function ImagenProtegida({ url, alt }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    fetch(url, { headers: solicitudesCambioService.authHeader() })
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!src) return <div style={{ width: 120, height: 80, background: "#f3f4f6" }} />;
  return <Image src={src} alt={alt} width={120} height={80} style={{ objectFit: "cover" }} />;
}

export default function ProgramadorPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [estadoFiltro, setEstadoFiltro] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [idSeleccionado, setIdSeleccionado] = useState(null);

  useEffect(() => {
    setLoading(true);
    solicitudesCambioService
      .listarTodas({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, estado: estadoFiltro })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [page, estadoFiltro]);

  const columns = useMemo(
    () => [
      { title: "Folio", dataIndex: "folio", key: "folio", width: 110 },
      { title: "Título", dataIndex: "titulo", key: "titulo" },
      {
        title: "Tipo",
        key: "tipo",
        render: (_, r) => TIPO_LABEL[r.tipo] || r.tipo,
      },
      {
        title: "Prioridad",
        key: "prioridad",
        render: (_, r) => <DataBadge color={PRIORIDAD_COLOR[r.prioridad]}>{PRIORIDAD_LABEL[r.prioridad] || r.prioridad}</DataBadge>,
      },
      {
        title: "Estado",
        key: "estado",
        render: (_, r) => <DataBadge color={ESTADO_COLOR[r.estado]}>{ESTADO_LABEL[r.estado] || r.estado}</DataBadge>,
      },
      { title: "Usuario", key: "usuario", render: (_, r) => r.usuario_nombre || `#${r.id_user}` },
      { title: "Enviada", key: "created_at", render: (_, r) => formatFecha(r.created_at) },
      {
        title: "Acciones",
        key: "acciones",
        align: "right",
        render: (_, r) => (
          <Button type="primary" onClick={() => setIdSeleccionado(r.id)}>
            Ver detalles
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <main className="trib-main">
      <div className="trib-wrap">
        <div className="laboral-filters-panel">
          <div className="laboral-panel-header">
            <h1 className="laboral-panel-title">Programador · Solicitudes de cambio</h1>
            <p className="laboral-panel-subtitle">
              Reporte de errores, cambios y nuevas funciones solicitados por los usuarios del sistema.
            </p>
          </div>
          <div className="laboral-filter-row-2">
            <div className="laboral-field">
              <div className="laboral-field-label">ESTADO</div>
              <Select
                allowClear
                placeholder="Todos"
                style={{ minWidth: 220 }}
                value={estadoFiltro}
                onChange={(v) => {
                  setEstadoFiltro(v);
                  setPage(1);
                }}
                options={Object.entries(ESTADO_LABEL).map(([value, label]) => ({ value, label }))}
              />
            </div>
          </div>
        </div>

        <Card bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={items}
            rowKey={(r) => r.id}
            pagination={false}
            loading={loading}
          />
          <div className="p-3 d-flex align-items-center justify-content-between" style={{ borderTop: "1px solid #eef2f7" }}>
            <small className="text-secondary">{total} solicitudes</small>
            <AppPagination current={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} size="sm" />
          </div>
        </Card>
      </div>

      <DetalleModal idSolicitud={idSeleccionado} onClose={() => setIdSeleccionado(null)} />
    </main>
  );
}
